import { useState, useEffect, useCallback } from 'react';
import { getExchangeRates } from '../utils/currencyConverter';
import { getPreferences, savePreferences } from '../utils/firestoreStorage';

// adjustmentFactor is stored as a percentage (e.g. 5 = +5%).
// Applied as: convertedTotal = rawConverted * (1 + pct/100)

function applyConversion(expense, usdRates, defaultCurrency, adjustmentPct) {
  const fromCurrency = expense.currency || 'USD';
  const toCurrency = defaultCurrency;
  const num = parseFloat(expense.total) || 0;

  let conversionRate = 1;
  let convertedTotal = num;

  if (usdRates && fromCurrency !== toCurrency) {
    const fromRate = usdRates[fromCurrency] ?? 1; // 1 fromCurrency = fromRate USD
    const toRate   = usdRates[toCurrency]   ?? 1; // 1 toCurrency   = toRate   USD
    conversionRate = fromRate / toRate;            // 1 fromCurrency = conversionRate toCurrency
    convertedTotal = num * conversionRate;
  }

  const multiplier = fromCurrency !== toCurrency
    ? 1 + (parseFloat(adjustmentPct) || 0) / 100
    : 1;
  convertedTotal = convertedTotal * multiplier;

  return {
    ...expense,
    convertedTotal:    Math.round(convertedTotal * 100) / 100,
    convertedCurrency: toCurrency,
    conversionRate:    Math.round(conversionRate * 1e6) / 1e6,
    adjustmentFactor:  adjustmentPct,
    conversionDate:    new Date().toISOString(),
  };
}

export function useCurrency(uid) {
  const [usdRates,         setUsdRates]         = useState(null);
  const [defaultCurrency,  setDefaultCurrencyS]  = useState('USD');
  const [adjustmentFactor, setAdjustmentFactorS] = useState(0);
  const [ratesLoading,     setRatesLoading]      = useState(true);
  const [ratesError,       setRatesError]        = useState(null);

  // Load saved preferences (currency + adjustment factor)
  useEffect(() => {
    getPreferences(uid).then(prefs => {
      setDefaultCurrencyS(prefs.defaultCurrency ?? 'USD');
      setAdjustmentFactorS(prefs.adjustmentFactor ?? 0);
    });
  }, [uid]);

  // Fetch exchange rates (cached in Firestore / localStorage)
  useEffect(() => {
    setRatesLoading(true);
    setRatesError(null);
    getExchangeRates(uid)
      .then(rates => setUsdRates(rates))
      .catch(err => {
        console.error('Exchange rate fetch failed:', err);
        setRatesError(err.message);
      })
      .finally(() => setRatesLoading(false));
  }, [uid]);

  // Convert a single expense and stamp it with audit fields.
  // Uses the current rates, defaultCurrency, and adjustmentFactor from state.
  const convertExpense = useCallback((expense) => {
    return applyConversion(expense, usdRates, defaultCurrency, adjustmentFactor);
  }, [usdRates, defaultCurrency, adjustmentFactor]);

  // Re-convert an entire array of expenses (e.g. after prefs change).
  const reconvertAllExpenses = useCallback((expenses) => {
    return expenses.map(e => applyConversion(e, usdRates, defaultCurrency, adjustmentFactor));
  }, [usdRates, defaultCurrency, adjustmentFactor]);

  // Persist changed preferences and update local state.
  const setPreferences = useCallback(async ({ defaultCurrency: dc, adjustmentFactor: af }) => {
    setDefaultCurrencyS(dc);
    setAdjustmentFactorS(af);
    await savePreferences(uid, { defaultCurrency: dc, adjustmentFactor: af });
  }, [uid]);

  return {
    defaultCurrency,
    adjustmentFactor,
    setPreferences,
    convertExpense,
    reconvertAllExpenses,
    usdRates,
    ratesLoading,
    ratesError,
  };
}
